import React, { useState, useRef, useCallback, useEffect, useId } from 'react';
import { Camera, X, Loader2, AlertCircle } from 'lucide-react';

/**
 * Reusable mobile-first barcode camera scanner button.
 * Uses html5-qrcode (already bundled).
 *
 * Props:
 *   onDetected(code: string)   — fired when a barcode is decoded
 *   title?: string             — tooltip + modal heading
 *   iconOnly?: boolean         — render icon-only button (default false)
 *   className?: string         — optional extra classes on the trigger button
 *   testId?: string            — data-testid on the trigger
 */
export default function BarcodeScannerButton({ onDetected, title = 'Scan barcode', iconOnly = false, className = '', testId = 'scan-barcode-btn' }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const mountRef = useRef(null);
  const scannerRef = useRef(null);
  const uid = useId();
  const mountId = `barcode-scanner-${uid.replace(/[:]/g, '')}`;

  const stop = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_e) { /* ignore */ }
      try { scannerRef.current.clear(); } catch (_e) { /* ignore */ }
      scannerRef.current = null;
    }
  }, []);

  const close = useCallback(async () => {
    await stop();
    setOpen(false);
    setError('');
    setStarting(false);
  }, [stop]);

  const start = useCallback(async () => {
    setError('');
    setStarting(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      // The mount div is created when `open===true`; give it a tick to paint.
      await new Promise(r => setTimeout(r, 50));
      if (!mountRef.current) {
        throw new Error('Scanner mount not ready');
      }
      const scanner = new Html5Qrcode(mountRef.current.id);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 160 } },
        (decoded) => {
          const code = String(decoded || '').trim();
          if (!code) return;
          onDetected(code);
          close();
        },
        () => { /* per-frame decode failure — suppress */ },
      );
    } catch (e) {
      setError(e?.message || 'Camera not available. Grant camera permission or try a different browser.');
    } finally {
      setStarting(false);
    }
  }, [onDetected, close]);

  // When the modal opens, kick off camera start
  useEffect(() => {
    if (open) start();
    return () => { stop(); };
  }, [open, start, stop]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className || 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0052FF] hover:bg-[#0040CC] text-white text-sm font-semibold transition-colors'}
        title={title}
        data-testid={testId}
      >
        <Camera className="w-4 h-4" />
        {!iconOnly && <span>Scan</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="scanner-modal"
          onClick={close}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#0052FF]" />
                <h3 className="text-base font-semibold text-[#0F172A]">{title}</h3>
              </div>
              <button onClick={close} className="text-slate-400 hover:text-slate-700 p-1" data-testid="scanner-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative bg-black">
              <div
                id={mountId}
                ref={mountRef}
                style={{ width: '100%', minHeight: 320 }}
              />
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center text-white/80">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-xs">Starting camera…</span>
                  </div>
                </div>
              )}
            </div>

            {error ? (
              <div className="flex items-start gap-2 px-4 py-3 bg-red-50 text-red-700 text-sm border-t border-red-200" data-testid="scanner-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ) : (
              <div className="px-4 py-2 text-center text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
                Point the rear camera at the barcode. The scan auto-closes on success.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
